import React from 'react';

// Cuentas con proveedores — helpers compartidos por las pantallas del módulo.
// Convención de signo (igual que el backend): debit - credit = lo que debemos.

// Owner-only: mismo id que el lock del backend (routes/supplierAccounts.js).
export const SUPPLIER_ACCOUNTS_USER_IDS = new Set(['eafdff80-02d2-4eef-9e5c-19f57761b85d']);

export const canUseSupplierAccounts = (user, isImpersonating) =>
  !!user && SUPPLIER_ACCOUNTS_USER_IDS.has(user.id) && !isImpersonating;

export function fmtMoney(n, currency = 'USD', { sign = false } = {}) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  const abs = Math.abs(num).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sym = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : '$';
  const s = num < 0 ? '−' : sign && num > 0 ? '+' : '';
  return `${s}${sym} ${abs}`;
}

export function fmtNumber(n, decimals = 2) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
}

// Número tipeado a la argentina: "3.030.000", "1.450,50", "1450.5", "2 150".
// Con coma → la coma es el decimal y los puntos son miles. Sin coma → un punto
// seguido de exactamente 3 dígitos (o varios puntos) es de miles; si no, decimal.
export function parseNum(raw) {
  if (raw == null) return NaN;
  let s = String(raw).trim().replace(/\s|US\$|U\$D|\$/g, '');
  if (!s) return NaN;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if ((s.match(/\./g) || []).length > 1 || /^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

// Trabajo registrado sin monto todavía (p.ej. el contratista cotiza después).
// Una visita marcada "(sin cargo aparte)" (incluida en un paquete, o que el
// proveedor no cobró) no es un trabajo pendiente de precio.
export const isUnpriced = (e) => !(Number(e.debit) > 0) && !(Number(e.credit) > 0) && !/sin cargo aparte/i.test(e.notes || '');

export const amountOf = (e) => Math.round(((Number(e.debit) || 0) - (Number(e.credit) || 0)) * 100) / 100;

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(`${String(iso).slice(0, 10)}T00:00:00`).getTime()) / 86400000);
};

// Tipos de movimiento. `direction` = el lado natural del tipo (debit: aumenta
// lo que debo; credit: lo reduce). 'other' deja elegir.
export const ENTRY_TYPES = [
  { kind: 'product', direction: 'debit', icon: 'Package' },
  { kind: 'service', direction: 'debit', icon: 'Wrench' },
  { kind: 'cash', direction: 'credit', icon: 'Banknote' },
  { kind: 'transfer', direction: 'credit', icon: 'ArrowLeftRight' },
  { kind: 'client_invoice', direction: 'credit', icon: 'Receipt' },
  { kind: 'return', direction: 'credit', icon: 'Undo2' },
  { kind: 'freight', direction: 'debit', icon: 'Truck' },
  { kind: 'tax', direction: 'debit', icon: 'Percent' },
  { kind: 'other', direction: null, icon: 'MoreHorizontal' },
];

export const ALL_KINDS = ['product', 'service', 'cash', 'transfer', 'client_invoice', 'tax', 'withholding', 'check', 'return', 'freight', 'fee', 'opening', 'adjustment', 'other'];

// Agrupa asientos en "filas de conciliación": una por operación, o una por
// asiento suelto. status: matched (las dos sumas dan igual), diff (agrupado
// pero no cierra), only_mine / only_theirs (sin par), void (anulación interna).
export function buildRows(entries, operations) {
  const ops = new Map((operations || []).map((o) => [o.id, o]));
  const groups = new Map();
  for (const e of entries) {
    if (e.voided_at) continue;
    const key = e.operation_id && ops.has(e.operation_id) ? `op:${e.operation_id}` : `e:${e.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  const rows = [];
  for (const [key, list] of groups) {
    const mine = list.filter((e) => e.side === 'mine');
    const theirs = list.filter((e) => e.side === 'theirs');
    const sumMine = Math.round(mine.reduce((s, e) => s + amountOf(e) * 100, 0)) / 100;
    const sumTheirs = Math.round(theirs.reduce((s, e) => s + amountOf(e) * 100, 0)) / 100;
    const op = key.startsWith('op:') ? ops.get(key.slice(3)) : null;
    let status;
    if (!op && list.every((e) => e.is_projected)) status = 'projected';
    else if (!op) status = mine.length ? 'only_mine' : 'only_theirs';
    else if ((!mine.length || !theirs.length) && Math.abs(sumMine + sumTheirs) < 0.005) status = 'void';
    else status = Math.abs(sumMine - sumTheirs) < 0.005 ? 'matched' : 'diff';
    const dates = list.map((e) => e.entry_date).sort();
    rows.push({
      key,
      op,
      mine,
      theirs,
      sumMine,
      sumTheirs,
      diff: Math.round((sumTheirs - sumMine) * 100) / 100,
      status,
      firstDate: dates[0],
      lastDate: dates[dates.length - 1],
      validated: list.every((e) => e.validated_at),
      awaiting: mine.some((e) => e.expect_in_theirs) && !op,
      unpriced: list.some(isUnpriced),
      projected: list.some((e) => e.is_projected),
      years: [...new Set(list.map((e) => e.entry_date.slice(0, 4)))],
      missing: theirs.some((e) => e.missing_from_import_at),
      text: list.map((e) => `${e.description || ''} ${e.ref || ''} ${e.notes || ''} ${e.company_name || ''}`).join(' ').toLowerCase(),
    });
  }
  rows.sort((a, b) => (a.lastDate < b.lastDate ? 1 : a.lastDate > b.lastDate ? -1 : 0));
  return rows;
}

// El PDF del proveedor repite la referencia al principio de la descripción
// ("INV/2026/06218 · INV/2026/06218"): se muestra una sola vez.
export function cleanDesc(e) {
  let d = String(e?.description || '').trim();
  if (e?.ref && d.startsWith(e.ref)) d = d.slice(e.ref.length).replace(/^[\s·:-]+/, '');
  return d;
}

// Pantalla angosta (celular): las tablas de varias columnas pasan a tarjetas.
export function useIsNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = React.useState(() => typeof window !== 'undefined' && window.innerWidth <= breakpoint);
  React.useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

export function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
  return Promise.resolve();
}
